/**
 * tdesign v1.0.6
 * (c) 2023 TDesign Group
 * @license MIT
 */

import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { defineComponent, getCurrentInstance, inject, computed, h, toRefs, resolveComponent, openBlock, createElementBlock, normalizeClass, createVNode, Fragment, createBlock, createCommentVNode, createElementVNode, withModifiers, normalizeStyle } from 'vue';
import { CheckCircleFilledIcon, CircleIcon, MinusCircleFilledIcon, MinusRectangleFilledIcon, CheckRectangleFilledIcon } from 'tdesign-icons-vue-next';
import config from '../config.js';
import CheckboxProps from './props.js';
import '../shared/index.js';
import TNodeComponent from '../shared/render-tnode.js';
import { useDefault } from '../shared/useDefault/index.js';
import { renderContent, renderTNode } from '../shared/render.js';
import '../shared/functions.js';
import '../shared/util.js';
import 'lodash/isNumber';
import '../shared/component.js';
import '../shared/constants.js';
import '../shared/useToggle/index.js';
import '../shared/useCountDown/index.js';
import '@babel/runtime/helpers/asyncToGenerator';
import '@babel/runtime/regenerator';
import '@vueuse/core';
import '../shared/useCountDown/utils.js';
import '../shared/useChildSlots/index.js';
import '@babel/runtime/helpers/toConsumableArray';
import '../shared/useVModel/index.js';
import 'lodash/kebabCase';
import '../shared/useTouch/index.js';
import '../shared/useScrollParent/index.js';
import '../shared/useExpose/index.js';
import '../shared/useTest/index.js';
import '../shared/useClickAway/index.js';
import 'lodash/isArray';
import '../shared/useGesture/index.js';
import '@use-gesture/vanilla';
import '../shared/hover.js';
import '@babel/runtime/helpers/typeof';
import 'lodash/camelCase';

var MinusLineFilledIcon = "data:image/svg+xml,%3Csvg%20width%3D%2224%22%20height%3D%2225%22%20viewBox%3D%220%200%2024%2025%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%20%20%20%20%3Cpath%20d%3D%22M5%2012.5C5%2011.6716%205.69645%2011%206.55556%2011H17.4444C18.3036%2011%2019%2011.6716%2019%2012.5C19%2013.3284%2018.3036%2014%2017.4444%2014H6.55556C5.69645%2014%205%2013.3284%205%2012.5Z%22%20fill%3D%22%230052D9%22%2F%3E%3C%2Fsvg%3E";

var CheckLineFilledIcon = "data:image/svg+xml,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%20%20%20%20%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M20.0606%206.93921C20.6463%207.525%2020.6464%208.47475%2020.0606%209.06053L11.7677%2017.3535C10.7914%2018.3296%209.20843%2018.3297%208.23212%2017.3534L3.93923%2013.0606C3.35344%2012.4748%203.35343%2011.525%203.93921%2010.9392C4.52499%2010.3534%205.47474%2010.3534%206.06053%2010.9392L9.99995%2014.8786L17.9392%206.93922C18.525%206.35343%2019.4748%206.35343%2020.0606%206.93921Z%22%20fill%3D%22%230052D9%22%2F%3E%3C%2Fsvg%3E";

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var prefix = config.prefix;
var name = "".concat(prefix, "-checkbox");
var script = defineComponent({
  name: name,
  components: {
    TNode: TNodeComponent
  },
  props: _objectSpread(_objectSpread({}, CheckboxProps), {}, {
    borderless: {
      type: Boolean,
      value: false
    }
  }),
  emits: ["update:checked", "update:modelValue", "change"],
  setup: function setup(props, context) {
    var _useDefault = useDefault(props, context.emit, "checked", "change"),
      _useDefault2 = _slicedToArray(_useDefault, 2),
      innerChecked = _useDefault2[0],
      setInnerChecked = _useDefault2[1];
    var internalInstance = getCurrentInstance();
    var checkboxGroup = inject("checkboxGroup", void 0);
    var labelContent = computed(function () {
      return renderContent(internalInstance, "label", "default");
    });
    var checkboxContent = computed(function () {
      return renderTNode(internalInstance, "content");
    });
    var indeterminate = computed(function () {
      if (props.checkAll && checkboxGroup != null) return checkboxGroup.checkAllStatus.value === "indeterminate";
      return props.indeterminate;
    });
    var isIconArray = Array.isArray(props.icon);
    var defaultCheckIcons = [h(CheckCircleFilledIcon), h(CircleIcon)];
    var checkIcons = computed(function () {
      if (isIconArray && props.icon.length > 1) {
        return props.icon.map(function (icon) {
          return typeof icon === "string" ? h("img", {
            class: "".concat(name, "__icon-image"),
            src: icon
          }) : icon;
        });
      }
      return defaultCheckIcons;
    });
    var checkedIcon = computed(function () {
      if (props.icon === "circle" || props.icon === true) return indeterminate.value ? h(MinusCircleFilledIcon) : h(CheckCircleFilledIcon);
      if (props.icon === "rectangle") return indeterminate.value ? h(MinusRectangleFilledIcon) : h(CheckRectangleFilledIcon);
      if (props.icon === "line") return indeterminate.value ? h("img", {
        src: MinusLineFilledIcon
      }) : h("img", {
        src: CheckLineFilledIcon
      });
      return null;
    });
    var isChecked = computed(function () {
      if (props.checkAll) {
        var checkAllStatus = checkboxGroup === null || checkboxGroup === void 0 ? void 0 : checkboxGroup.checkAllStatus.value;
        return checkAllStatus === "checked" || checkAllStatus === "indeterminate";
      }
      if (checkboxGroup != null && props.value != null) {
        var _checkboxGroup$checke;
        return !!((_checkboxGroup$checke = checkboxGroup.checkedSet.value) !== null && _checkboxGroup$checke !== void 0 && _checkboxGroup$checke.has(props.value));
      }
      return innerChecked.value;
    });
    var isDisabled = computed(function () {
      if (checkboxGroup !== null && checkboxGroup !== void 0 && checkboxGroup.max.value) return checkboxGroup.max.value <= checkboxGroup.innerValue.value.length && !isChecked.value;
      if (props.disabled != null) return props.disabled;
      return !!(checkboxGroup !== null && checkboxGroup !== void 0 && checkboxGroup.disabled.value);
    });
    var handleChange = function handleChange(e, source) {
      if (isDisabled.value) return;
      if (source === "text" && props.contentDisabled) return;
      var value = !isChecked.value;
      setInnerChecked(value, {
        e: e
      });
      e.stopPropagation();
      if (checkboxGroup && checkboxGroup !== null && checkboxGroup !== void 0 && checkboxGroup.onCheckedChange) {
        checkboxGroup.onCheckedChange({
          checked: value,
          checkAll: props.checkAll,
          e: e,
          option: props
        });
      }
    };
    return _objectSpread(_objectSpread({}, toRefs(props)), {}, {
      name: name,
      checkedIcon: checkedIcon,
      isIconArray: isIconArray,
      checkIcons: checkIcons,
      labelContent: labelContent,
      checkboxContent: checkboxContent,
      isChecked: isChecked,
      isDisabled: isDisabled,
      indeterminate: indeterminate,
      handleChange: handleChange
    });
  }
});

var _hoisted_1 = {
  key: 1,
  class: "placeholder"
};
function render(_ctx, _cache, $props, $setup, $data, $options) {
  var _normalizeClass2, _normalizeClass3, _normalizeClass4, _normalizeClass5, _normalizeClass6;
  var _component_t_node = resolveComponent("t-node");
  return openBlock(), createElementBlock("div", {
    class: normalizeClass((_normalizeClass2 = {}, _defineProperty(_normalizeClass2, "".concat(_ctx.name), true), _defineProperty(_normalizeClass2, "".concat(_ctx.name, "--").concat(_ctx.placement), true), _defineProperty(_normalizeClass2, "".concat(_ctx.name, "--checked"), _ctx.isChecked), _defineProperty(_normalizeClass2, "".concat(_ctx.name, "--block"), _ctx.block), _normalizeClass2)),
    onClick: _cache[1] || (_cache[1] = function () {
      return _ctx.handleChange && _ctx.handleChange.apply(_ctx, arguments);
    })
  }, [_ctx.icon ? (openBlock(), createElementBlock("div", {
    key: 0,
    class: normalizeClass((_normalizeClass3 = {}, _defineProperty(_normalizeClass3, "".concat(_ctx.name, "__icon"), true), _defineProperty(_normalizeClass3, "".concat(_ctx.name, "__icon--").concat(_ctx.placement), true), _defineProperty(_normalizeClass3, "".concat(_ctx.name, "__icon--checked"), _ctx.isChecked), _defineProperty(_normalizeClass3, "".concat(_ctx.name, "__icon--disabled"), _ctx.isDisabled), _normalizeClass3))
  }, [_ctx.isIconArray ? (openBlock(), createElementBlock("div", {
    key: 0,
    class: normalizeClass("".concat(_ctx.name, "__icon"))
  }, [createVNode(_component_t_node, {
    content: _ctx.checkIcons[_ctx.isChecked ? 0 : 1],
    class: normalizeClass("".concat(_ctx.name, "__icon-wrapper"))
  }, null, 8, ["content", "class"])], 2)) : (openBlock(), createElementBlock(Fragment, {
    key: 1
  }, [_ctx.isChecked ? (openBlock(), createBlock(_component_t_node, {
    key: 0,
    content: _ctx.checkedIcon,
    class: normalizeClass("".concat(_ctx.name, "__icon-wrapper"))
  }, null, 8, ["content", "class"])) : (openBlock(), createElementBlock(Fragment, {
    key: 1
  }, [_ctx.icon === "circle" || _ctx.icon === true || _ctx.icon === "rectangle" ? (openBlock(), createElementBlock("div", {
    key: 0,
    class: normalizeClass((_normalizeClass4 = {}, _defineProperty(_normalizeClass4, "".concat(_ctx.name, "__icon-circle"), _ctx.icon === true), _defineProperty(_normalizeClass4, "".concat(_ctx.name, "__icon-").concat(_ctx.icon), typeof _ctx.icon === "string"), _defineProperty(_normalizeClass4, "".concat(_ctx.name, "__icon-").concat(_ctx.icon, "--disabled"), _ctx.isDisabled), _normalizeClass4))
  }, null, 2)) : createCommentVNode("", true), _ctx.icon === "line" ? (openBlock(), createElementBlock("div", _hoisted_1)) : createCommentVNode("", true)], 64))], 64))], 2)) : createCommentVNode("", true), createElementVNode("div", {
    class: normalizeClass("".concat(_ctx.name, "__content")),
    onClick: _cache[0] || (_cache[0] = withModifiers(function (e) {
      return _ctx.handleChange(e, "text");
    }, ["stop"]))
  }, [createElementVNode("div", {
    class: normalizeClass((_normalizeClass5 = {}, _defineProperty(_normalizeClass5, "".concat(_ctx.name, "__title"), true), _defineProperty(_normalizeClass5, "".concat(_ctx.name, "__title--checked"), _ctx.isChecked), _defineProperty(_normalizeClass5, "".concat(_ctx.name, "__title--disabled"), _ctx.isDisabled), _normalizeClass5)),
    style: normalizeStyle({
      "-webkit-line-clamp": _ctx.maxLabelRow
    })
  }, [createVNode(_component_t_node, {
    content: _ctx.labelContent
  }, null, 8, ["content"])], 6), createElementVNode("div", {
    class: normalizeClass((_normalizeClass6 = {}, _defineProperty(_normalizeClass6, "".concat(_ctx.name, "__description"), true), _defineProperty(_normalizeClass6, "".concat(_ctx.name, "__description--disabled"), _ctx.isDisabled), _normalizeClass6)),
    style: normalizeStyle({
      "-webkit-line-clamp": _ctx.maxContentRow
    })
  }, [createVNode(_component_t_node, {
    content: _ctx.checkboxContent
  }, null, 8, ["content"])], 6)], 2), !_ctx.borderless ? (openBlock(), createElementBlock("div", {
    key: 1,
    class: normalizeClass("".concat(_ctx.name, "__border ").concat(_ctx.name, "__border--").concat(_ctx.placement))
  }, null, 2)) : createCommentVNode("", true)], 2);
}

script.render = render;

export { script as default };
//# sourceMappingURL=checkbox.js.map
